import axiosInstance from "@/axios";

export const state = {
    ticketsData: [],
    ticketsLoading: false,
    ticketsMsg: null,
    statusesData: [],
    statusesLoading: false,
    statusesMsg: null,
    pagination: {
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: 10,
        hasNext: false,
        hasPrev: false
    }
}

export const mutations = {
    SET_TICKETS_DATA(state, ticketsData) {
        state.ticketsData = ticketsData
    },
    SET_LOADING(state, ticketsLoading) {
        state.ticketsLoading = ticketsLoading
    },
    SET_TICKETS_MSG(state, ticketsMsg) {
        state.ticketsMsg = ticketsMsg
    },
    SET_STATUSES_DATA(state, statusesData) {
        state.statusesData = statusesData
    },
    SET_STATUSES_LOADING(state, statusesLoading) {
        state.statusesLoading = statusesLoading
    },
    SET_STATUSES_MSG(state, statusesMsg) {
        state.statusesMsg = statusesMsg
    },
    SET_PAGINATION(state, pagination) {
        // Ensure proper reactivity by creating a new object reference
        state.pagination = { ...pagination }
    },
    DELETE_TICKET(state, ticketId) {
        if (Array.isArray(state.ticketsData)) {
            state.ticketsData = state.ticketsData.filter(ticket => ticket.id !== ticketId);
        }
    },
}

export const getters = {
    ticketsData: state => state.ticketsData,
    ticketsLoading: state => state.ticketsLoading,
    ticketsMsg: state => state.ticketsMsg,
    statusesData: state => state.statusesData,
    statusesLoading: state => state.statusesLoading,
    statusesMsg: state => state.statusesMsg,
    ticketsPagination: state => state.pagination,
}

export const actions = {
    async getTicketsData({ commit }, params = {}) {
        const { page = 1, perPage = 10, ...filterParams } = params;

        commit('SET_LOADING', true)

        try {
            const response = await axiosInstance({ requiresAuth: true }).get(`/agent/tickets`, {
                params: { page, perPage, ...filterParams }
            });

            // Use the full response object, not just response.data
            let responseData = response.data;

            // Check if the response has pagination structure at the top level
            if (response.data && response.links && response.meta) {
                // Restructure to match expected format
                responseData = {
                    data: response.data,
                    links: response.links,
                    meta: response.meta
                };
            }

            let ticketsData = [];
            let paginationData = {
                currentPage: parseInt(page) || 1,
                totalPages: 1,
                totalItems: 0,
                itemsPerPage: parseInt(perPage) || 10,
                hasNext: false,
                hasPrev: false
            };

            // Enhanced pagination extraction with proper meta and links handling

            // TEMPORARY FIX: If we get a direct array, try different API parameters
            if (Array.isArray(responseData) && responseData.length === parseInt(perPage)) {
                try {
                    // Try alternative parameter names
                    const alternativeResponse = await axiosInstance({ requiresAuth: true }).get(`/agent/tickets`, {
                        params: { page: page, per_page: perPage, ...filterParams }  // Try snake_case
                    });

                    if (alternativeResponse.data && !Array.isArray(alternativeResponse.data)) {
                        // Use the alternative response instead
                        responseData = alternativeResponse.data;
                    }
                } catch (error) {
                    // Alternative API call failed, using original response
                }
            }

            if (responseData && responseData.data && Array.isArray(responseData.data)) {
                // Standard Laravel/API paginated response: { data: [...], meta: {...}, links: {...} }
                ticketsData = responseData.data;

                const meta = responseData.meta;
                const links = responseData.links;

                if (meta) {
                    // Use meta object for pagination info (Laravel style)
                    paginationData = {
                        currentPage: parseInt(meta.current_page) || parseInt(page) || 1,
                        totalPages: parseInt(meta.last_page) || 1,
                        totalItems: parseInt(meta.total) || ticketsData.length,
                        itemsPerPage: parseInt(meta.per_page) || parseInt(perPage) || 10,
                        hasNext: !!(links?.next || (meta.current_page && meta.last_page && meta.current_page < meta.last_page)),
                        hasPrev: !!(links?.prev || (meta.current_page && meta.current_page > 1))
                    };
                } else {
                    // Fallback to response-level pagination data
                    paginationData = {
                        currentPage: parseInt(responseData.current_page) || parseInt(page) || 1,
                        totalPages: parseInt(responseData.last_page) || Math.ceil((parseInt(responseData.total) || ticketsData.length) / parseInt(perPage)) || 1,
                        totalItems: parseInt(responseData.total) || ticketsData.length,
                        itemsPerPage: parseInt(responseData.per_page) || parseInt(perPage) || 10,
                        hasNext: !!(responseData.next_page_url || (responseData.current_page && responseData.last_page && responseData.current_page < responseData.last_page)),
                        hasPrev: !!(responseData.prev_page_url || (responseData.current_page && responseData.current_page > 1))
                    };
                }
            } else if (responseData && responseData.results && Array.isArray(responseData.results)) {
                // Django REST Framework style: { results: [...], count: N, next: URL, previous: URL }
                ticketsData = responseData.results;

                paginationData = {
                    currentPage: parseInt(page) || 1,
                    totalPages: Math.ceil((parseInt(responseData.count) || ticketsData.length) / parseInt(perPage)) || 1,
                    totalItems: parseInt(responseData.count) || ticketsData.length,
                    itemsPerPage: parseInt(perPage) || 10,
                    hasNext: !!responseData.next,
                    hasPrev: !!responseData.previous
                };
            } else if (Array.isArray(responseData)) {
                // Direct array response (no pagination metadata) - treat as single page
                ticketsData = responseData;
                paginationData = {
                    currentPage: 1,
                    totalPages: 1,
                    totalItems: ticketsData.length,
                    itemsPerPage: ticketsData.length || parseInt(perPage) || 10,
                    hasNext: false,
                    hasPrev: false
                };
            } else {
                // Final fallback
                ticketsData = responseData?.data || responseData || [];
                const totalItems = ticketsData.length || 0;
                paginationData = {
                    currentPage: 1,
                    totalPages: Math.ceil(totalItems / parseInt(perPage)) || 1,
                    totalItems: totalItems,
                    itemsPerPage: parseInt(perPage) || 10,
                    hasNext: false,
                    hasPrev: false
                };
            }

            commit('SET_TICKETS_DATA', ticketsData);
            commit('SET_PAGINATION', paginationData);
            commit('SET_TICKETS_MSG', null);
        } catch (error) {
            commit('SET_TICKETS_MSG', error.response?.data?.message || 'Failed to fetch tickets');
            commit('SET_TICKETS_DATA', []);
        } finally {
            commit('SET_LOADING', false);
        }
    },
    async updateTicket({ commit }, updateTicket) {
        // Clear previous messages
        commit('SET_TICKETS_MSG', null);

        try {
            const response = await axiosInstance({ requiresAuth: true })
                .put(`/agent/tickets/${updateTicket.id}`, updateTicket);

            // Optionally refetch data or update the specific ticket in state
            return response.data.data;
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Failed to update ticket';
            commit('SET_TICKETS_MSG', errorMessage);
            throw error;
        }
    },
    async deleteTicket({ commit }, ticketId) {
        try {
            await axiosInstance({ requiresAuth: true })
                .delete(`/agent/tickets/${ticketId}`);

            // Remove from local state
            commit('DELETE_TICKET', ticketId);
            commit('SET_TICKETS_MSG', null);
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Failed to delete ticket';
            commit('SET_TICKETS_MSG', errorMessage);
            throw error;
        }
    },
    async getSimplifiedStatusesData({ commit }) {
        commit('SET_STATUSES_LOADING', true)
        try {
            const response = await axiosInstance({ requiresAuth: true }).get(`/agent/simplified/ticketStatuses`);

            const responseData = response.data;

            // For simplified endpoint, expect direct array or simple structure
            let statusesData;
            if (Array.isArray(responseData)) {
                statusesData = responseData;
            } else if (responseData && Array.isArray(responseData.data)) {
                statusesData = responseData.data;
            } else {
                statusesData = responseData || [];
            }

            commit('SET_STATUSES_DATA', statusesData);
            commit('SET_STATUSES_MSG', null);
        } catch (error) {
            commit('SET_STATUSES_MSG', error.response?.data?.message || 'Failed to fetch statuses');
        } finally {
            commit('SET_STATUSES_LOADING', false);
        }
    },
}